import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Label, Patient } from '@pixacare/domain';
import { FlattenedSequence } from '@pixacare/dtos';
import { FormAnswer } from '@pixacare/forms';
import { SadmHttpService } from '@services/http/sadm.http.service';
import { AddAnalysisDraft } from '@shared/models/draft/add-analysis-draft';
import { SadmEntityDraft } from '@shared/models/draft/sadm-entity-draft';
import { isUnsortedArrayEqual } from '@shared/utils/utils';
import { isEqual } from 'date-fns';
import { first } from 'rxjs';
import { AutoCompleteLabelModel } from 'src/app/shared/models/helpers/auto-complete-label-model';
import { sequencesActions } from 'src/app/shared/store/sequences/sequences.actions';
import { selectSequence } from 'src/app/shared/store/sequences/sequences.selectors';
import { PatientEditService } from '../../patient/patient-edit/patient-edit/patient-edit.service';
import { SequenceMediaUploadService } from '../services/sequence-media-upload.service';

type SequenceEditContext = {
  updatedPatient: Patient;
  updatedLabels: AutoCompleteLabelModel[];
  updatedDate: Date;
  updatedDescription: string;
  removedMediasId: number[];
  newMedias: File[];
  sequence?: FlattenedSequence;
  clientCode: string;
  analysisFormAnswer: FormAnswer;
  protocolCreationFormAnswer?: FormAnswer;
  sadmEntityId: number;
  sadmEntityDraft: SadmEntityDraft;
  departmentIds: number[];
};

@Injectable()
export class SequenceEditService {
  private readonly store = inject(Store);
  private readonly mediaUploadService = inject(SequenceMediaUploadService);
  private readonly sadmHttpService = inject(SadmHttpService);

  isSequenceTouched(
    baseSequence: FlattenedSequence,
    {
      updatedPatient,
      updatedLabels,
      updatedDate,
      updatedDescription,
      removedMediasId,
      newMedias,
      analysisFormAnswer,
      sadmEntityId,
      departmentIds: updatedDepartmentIds,
    }: SequenceEditContext,
  ): boolean {
    return (
      !isEqual(updatedDate, baseSequence.sequenceInstance.createdOn) ||
      this.hasLabelChanged(baseSequence, updatedLabels) ||
      baseSequence.sequenceInstance.description !== updatedDescription ||
      removedMediasId.length > 0 ||
      newMedias.length > 0 ||
      !PatientEditService.compare(baseSequence.patientInstance, updatedPatient) ||
      baseSequence.patientInstance?.businessIdentifier !== updatedPatient?.businessIdentifier ||
      baseSequence.sequenceInstance.formAnswer !== analysisFormAnswer ||
      baseSequence.sequenceInstance.sadmEntityId !== sadmEntityId ||
      baseSequence.sequenceInstance.departmentIds !== updatedDepartmentIds
    );
  }

  save(payload: SequenceEditContext): void {
    this.handlePatchSequence(payload);
    this.handlePatientEdition(payload);
    this.handleLabelsEdition(payload);
    this.handleMediasEdition(payload);
    this.handleAnalysisEdition(payload);
    this.handleDepartmentsEdition(payload);
  }

  private handlePatientEdition({ sequence, updatedPatient, clientCode }: SequenceEditContext): void {
    if (
      !PatientEditService.compare(sequence.patientInstance, updatedPatient) ||
      sequence.patientInstance?.businessIdentifier !== updatedPatient?.businessIdentifier
    ) {
      this.store.dispatch(
        sequencesActions.updateSequencePatient({
          clientCode,
          sequenceId: sequence.sequenceInstance.id,
          patient: updatedPatient,
        }),
      );
    }
  }

  private hasLabelChanged(sequence: FlattenedSequence, updatedLabels: AutoCompleteLabelModel[]): boolean {
    if (sequence.labels.length !== updatedLabels.length) {
      return true;
    }

    const labelWords: { [label: string]: boolean } = updatedLabels.reduce(
      (acc, label) => {
        const word = typeof label.value === 'string' ? label.value.toUpperCase() : label.value.word?.toUpperCase();
        return { ...acc, [word]: true };
      },
      {} as { [label: string]: boolean },
    );

    return sequence.labels.some((label) => !labelWords[label.word]);
  }

  private handleLabelsEdition({ sequence, updatedLabels, clientCode }: SequenceEditContext): void {
    if (this.hasLabelChanged(sequence, updatedLabels)) {
      const createdLabels = updatedLabels
        .filter((updatedLabel) => !this.isLabel(updatedLabel.value))
        .map((updatedLabel) => updatedLabel.value.toString());

      const existingLabels = updatedLabels
        .filter((updatedLabel) => this.isLabel(updatedLabel.value))
        .map((updatedLabel) => (updatedLabel.value as Label).id);

      this.store.dispatch(
        sequencesActions.updateSequenceLabels({
          userId: sequence.sequenceInstance.createdBy,
          sequenceId: sequence.sequenceInstance.id,
          clientCode,
          existingLabels,
          createdLabels,
        }),
      );
    }
  }

  private handleMediasEdition({ sequence, removedMediasId, newMedias, clientCode }: SequenceEditContext): void {
    // Delete medias update
    removedMediasId.forEach((mediaId) => {
      this.store.dispatch(
        sequencesActions.deleteSequenceMedia({
          sequenceId: sequence.sequenceInstance.id,
          mediaId,
          clientCode,
        }),
      );
    });

    // Add medias update — same path as the create dialog: placeholders inserted up front, then
    // images through the multipart attach and videos through the chunked upload.
    this.mediaUploadService.attachMedias(clientCode, sequence, newMedias);
  }

  private handleAnalysisEdition({
    sequence,
    analysisFormAnswer,
    sadmEntityDraft,
    sadmEntityId,
    protocolCreationFormAnswer,
    clientCode,
  }: SequenceEditContext): void {
    const addAnalysisPayload: AddAnalysisDraft = {
      answers: analysisFormAnswer?.answers,
      special: analysisFormAnswer?.special,
      sadmClientId: sadmEntityDraft?.sadmClientId,
      sadmEntityName: sadmEntityDraft?.name,
      sadmEntityCreationFormAnswer: sadmEntityDraft?.entityCreationFormAnswer,
      sadmEntityId,
      protocolCreationFormAnswer,
    };

    if (!!sequence.sequenceInstance.formAnswer && !analysisFormAnswer) {
      // if there was an analysis and now there is none, it was DELETED
      this.store.dispatch(
        sequencesActions.deleteSequenceAnalysis({
          clientCode,
          sequenceId: sequence.sequenceInstance.id,
        }),
      );
    } else if (
      !sadmEntityDraft &&
      sequence.sequenceInstance.sadmEntityId === sadmEntityId &&
      analysisFormAnswer !== sequence.sequenceInstance.formAnswer
    ) {
      // if the sadmEntityId didn't change but the analysis did, it was EDITED
      this.store.dispatch(
        sequencesActions.editSequenceAnalysis({
          clientCode,
          sequenceId: sequence.sequenceInstance.id,
          formAnswer: analysisFormAnswer,
        }),
      );
      // During the sequence edition, if the analysis is deleted and created, we also need to handle the protocol separately.
      this.createProtocolForDraftedAnalysis(
        sadmEntityId,
        sadmEntityDraft?.sadmClientId,
        analysisFormAnswer,
        protocolCreationFormAnswer,
      );
    } else if (!sequence.sequenceInstance.formAnswer && !!analysisFormAnswer) {
      // if there was no analysis and now there is one, it was ADDED
      // protocol can't be added this way so we don't have to handle it
      this.store.dispatch(
        sequencesActions.addSequenceAnalysis({
          clientCode,
          sequenceId: sequence.sequenceInstance.id,
          payload: addAnalysisPayload,
        }),
      );
    } else if (
      !!sequence.sequenceInstance.formAnswer?.answers &&
      (sequence.sequenceInstance.sadmEntityId !== sadmEntityId || sadmEntityDraft) &&
      analysisFormAnswer
    ) {
      // special case : if the user deleted the current analysis then added a new one
      this.store.dispatch(
        sequencesActions.deleteSequenceAnalysis({
          clientCode,
          sequenceId: sequence.sequenceInstance.id,
        }),
      );

      // wait for deletion to be done before adding the new analysis
      this.store
        .select(selectSequence(sequence.sequenceInstance.id))
        .pipe(first((s) => !s.sequenceInstance.formAnswer))
        .subscribe(() => {
          this.store.dispatch(
            sequencesActions.addSequenceAnalysis({
              clientCode,
              sequenceId: sequence.sequenceInstance.id,
              payload: addAnalysisPayload,
            }),
          );
        });
    }
  }

  private createProtocolForDraftedAnalysis(
    sadmEntityId: number,
    sadmClientId: number,
    analysisFormAnswer: FormAnswer,
    protocolCreationFormAnswer?: FormAnswer,
  ): void {
    if (!sadmEntityId || analysisFormAnswer?._id || !protocolCreationFormAnswer) {
      return;
    }

    this.sadmHttpService
      .createProtocol({
        sadmEntityId,
        sadmClientId,
        answers: protocolCreationFormAnswer.answers,
        special: protocolCreationFormAnswer.special,
      })
      .subscribe({
        error: (error) => console.error('Error while creating the protocol of the edited analysis', error),
      });
  }

  private handlePatchSequence({ updatedDate, sequence, clientCode, updatedDescription }: SequenceEditContext): void {
    if (
      !isEqual(updatedDate, sequence.sequenceInstance.createdOn) ||
      updatedDescription !== sequence.sequenceInstance.description
    ) {
      this.store.dispatch(
        sequencesActions.patchSequence({
          sequence: {
            ...sequence.sequenceInstance,
            createdOn: updatedDate,
            description: updatedDescription,
          },
          clientCode,
        }),
      );
    }
  }

  private handleDepartmentsEdition({ sequence, departmentIds, clientCode }: SequenceEditContext): void {
    if (!sequence) {
      return;
    }

    const currentDepartmentIds = sequence.sequenceInstance.departmentIds ?? [];
    if (isUnsortedArrayEqual(currentDepartmentIds, departmentIds)) {
      return;
    }

    this.store.dispatch(
      sequencesActions.putSequenceDepartments({
        sequenceId: sequence.sequenceInstance.id,
        clientCode,
        departmentIds,
      }),
    );
  }

  private isLabel(object: Label | string): object is Label {
    return !!(object as Label).word;
  }
}
